import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../utils/api';
import i18n from '../i18n';

const AuthContext = createContext();

// Helper function to sync language with i18n and localStorage
const syncLanguage = (userLanguage) => {
  if (userLanguage && ['de', 'en'].includes(userLanguage)) {
    // Update localStorage first to ensure persistence
    localStorage.setItem('zenta_language', userLanguage);
    // Then update i18n
    if (i18n.language !== userLanguage) {
      i18n.changeLanguage(userLanguage);
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.setToken(token);
      
      // Add timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        console.warn('Auth check timeout - proceeding without user');
        setLoading(false);
      }, 10000); // 10 second timeout
      
      apiClient
        .getMe()
        .then((userData) => {
          clearTimeout(timeoutId);
          setUser(userData);
          // Sync language when user data is loaded
          if (userData.language) {
            syncLanguage(userData.language);
          }
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.warn('Auth check failed:', error);
          localStorage.removeItem('token');
          apiClient.setToken(null);
        })
        .finally(() => {
          clearTimeout(timeoutId);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const register = async (email, password) => {
    const data = await apiClient.register(email, password);
    setUser(data.user);
    // Sync language for new users (will use default 'de' if not set)
    if (data.user?.language) {
      syncLanguage(data.user.language);
    }
    return data;
  };

  const login = async (email, password) => {
    const data = await apiClient.login(email, password);
    setUser(data.user);
    // Sync language immediately after login
    if (data.user?.language) {
      syncLanguage(data.user.language);
    }
    return data;
  };

  const logout = () => {
    apiClient.logout();
    setUser(null);
    // Don't clear language on logout - let browser detection work for logged out users
  };

  // Function to update user data (used by other components)
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    if (updatedUser?.language) {
      syncLanguage(updatedUser.language);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
